import { axiosConfig } from "@/services/axiosConfig";
import type { SubmissionStatus } from "@/projects/lib/store";

const DASHBOARD_PATH = "/rajnagar_gyanmahostav/teacher-dashboard/";

export type TeacherSummaryCard = {
  key: string;
  label: string;
  value: number;
  trend_percent: number;
  trend_direction: "up" | "down" | string;
};

export type TeacherDashboardData = {
  activity_days: number;
  summary_cards: TeacherSummaryCard[];
  verification_activity: {
    title: string;
    subtitle: string;
    series: { date: string; assigned: number; verified: number }[];
  };
  completion: {
    title: string;
    subtitle: string;
    percent: number;
    verified: number;
    assigned: number;
    label: string;
    approval_rate_percent: number;
  };
  outcome_split: {
    title: string;
    subtitle: string;
    items: { status: string; label: string; count: number }[];
  };
  daily_throughput: {
    title: string;
    subtitle: string;
    series: { date: string; verified: number }[];
  };
  recent_assignments: {
    title: string;
    subtitle: string;
    items: {
      id: number | string;
      reference: string;
      title: string;
      student_name: string;
      student_id: string;
      assigned_at?: string;
      submitted_at?: string;
      status: string;
      status_display?: string;
    }[];
  };
};

type DashboardPayload =
  | TeacherDashboardData
  | { success?: boolean; message?: string; data?: TeacherDashboardData };

function unwrapDashboard(payload: DashboardPayload): TeacherDashboardData {
  if (payload && typeof payload === "object" && "data" in payload && payload.data) {
    return payload.data;
  }
  return payload as TeacherDashboardData;
}

export function formatDashboardDate(isoDate: string): string {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) {
    return isoDate;
  }
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export function normalizeDashboardStatus(raw?: string): SubmissionStatus {
  if (!raw) {
    return "Pending";
  }
  const v = raw.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (v === "pending") {
    return "Pending";
  }
  if (["assigned", "assigned_for_verification"].includes(v)) {
    return "Assigned for Verification";
  }
  if (["not_proper", "rejected"].includes(v)) {
    return "Not Proper";
  }
  if (["ready_for_reward", "approved", "verified"].includes(v)) {
    return "Ready for Reward";
  }
  if (
    raw === "Pending" ||
    raw === "Assigned for Verification" ||
    raw === "Not Proper" ||
    raw === "Ready for Reward"
  ) {
    return raw;
  }
  return "Pending";
}

export async function fetchTeacherDashboard(): Promise<TeacherDashboardData> {
  const { data } = await axiosConfig.get<DashboardPayload>(DASHBOARD_PATH, {
    headers: { Accept: "application/json" },
  });
  return unwrapDashboard(data);
}

export function getDashboardApiErrorMessage(error: unknown, fallback: string) {
  const err = error as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const data = err?.response?.data;
  if (!data) {
    return err?.message || fallback;
  }
  for (const key of ["message", "detail", "error"]) {
    const v = data[key];
    if (typeof v === "string" && v.trim()) {
      return v;
    }
  }
  return fallback;
}
